/**
 * Battalion Pulse — the live cross-board activity feed surfaced on the CO's
 * redesigned command-first dashboard. Reads only; aggregates recent
 * TaskActivity rows + small "today" rollups. CO-only at the route gate.
 *
 * Polling cadence and event limit live here next to the DTOs so a tuning
 * change is a single-file diff.
 */

/**
 * Event types the pulse surfaces. Curated subset of TaskActivityType — the
 * "noisy" types (LABELS_SET, CHECKLIST_*, RENAMED, DESCRIPTION_CHANGED,
 * ARCHIVED, UNARCHIVED) are excluded server-side because they drown out the
 * signal types (assignments, status changes, comments, mentions, updates).
 */
export type BattalionPulseEventType =
  | 'CREATED'
  | 'STATUS_CHANGED'
  | 'ASSIGNED'
  | 'UNASSIGNED'
  | 'COMMENTED'
  | 'UPDATE_REQUESTED'
  | 'UPDATE_POSTED'
  | 'MOVED'
  | 'PRIORITY_CHANGED'
  | 'DUE_DATE_CHANGED';

/** Server-side filter list — kept in sync with the union above. */
export const PULSE_INCLUDED_TYPES: readonly BattalionPulseEventType[] = [
  'CREATED',
  'STATUS_CHANGED',
  'ASSIGNED',
  'UNASSIGNED',
  'COMMENTED',
  'UPDATE_REQUESTED',
  'UPDATE_POSTED',
  'MOVED',
  'PRIORITY_CHANGED',
  'DUE_DATE_CHANGED',
] as const;

/** Most recent N events returned by the endpoint. */
export const BATTALION_PULSE_EVENT_LIMIT = 20;

/** Frontend poll cadence + server-side TtlCache window for the pulse endpoint. */
export const BATTALION_PULSE_POLL_MS = 30_000;

export interface BattalionPulseActorDto {
  userId: string | null;
  /** Already projected through `displayNameForUser` — CO renders as "CO". */
  name: string;
  rank: string | null;
  avatarColor: string | null;
}

export interface BattalionPulseTaskDto {
  taskId: string;
  title: string;
  boardId: string;
  boardName: string;
  /** Canonical deep-link path — `/tasks/${boardId}?task=${taskId}`. */
  linkPath: string;
}

export interface BattalionPulseEventDto {
  /** TaskActivity row id — stable React key. */
  id: string;
  type: BattalionPulseEventType;
  /** ISO8601 UTC. */
  createdAt: string;
  actor: BattalionPulseActorDto;
  task: BattalionPulseTaskDto;
  /** One-line human phrasing — "moved to In Progress", "commented", etc. */
  summary: string;
  /** Pass-through of TaskActivity.detail JSON. Null when the row carried no detail. */
  detail: Record<string, unknown> | null;
}

export interface BattalionPulseRollupsDto {
  /** Tasks created today (Asia/Dhaka day-boundary). */
  tasksCreatedToday: number;
  /** Tasks closed today (status.isDone = true, updatedAt today). */
  tasksClosedToday: number;
  /** UPDATE_REQUESTED activities with no matching UPDATE_POSTED response. */
  openUpdateRequests: number;
  /** Active tasks past their due date. */
  overdueOpen: number;
}

export interface BattalionPulseResponse {
  events: BattalionPulseEventDto[];
  rollups: BattalionPulseRollupsDto;
  /** ISO8601 — drives the "updated Ns ago" footer. */
  asOf: string;
}
